<template>
  <div>
    <div v-if="isAuthenticated" class="text-right ln-report-problem-container">
      <button type="button" class="btn btn-link p-0 mr-0 ln-list-ref ln-hover" style="color: rgb(0, 92, 191);" @click="reportProblem">
        <font-awesome-icon style="width: 12px" :icon="['fas', 'exclamation-triangle']" aria-hidden="true" />
        Pranešti apie problemą
      </button>
    </div>
    <h1 class="ln-doc-title ln-darkblue mt-3 mb-3">
      <button v-if="isClipboardAccessible" id="copy-button" type="button" class="ml-3 mb-4 ln-copy btn p-0 border-0 bg-transparent" @click="copyTitle" aria-label="Kopijuoti citavimui">
        <font-awesome-icon id="copy-icon" :icon="['fas', 'copy']" aria-hidden="true" />
        <b-tooltip target="copy-button" variant="secondary" placement="bottom" triggers="hover focus">Kopijuoti
          citavimui</b-tooltip>
      </button>
      {{ document.title }}
    </h1>
    <div class="mb-3"></div>
    <div class="ln-border-8 ln-bg-light">
      <div class="p-4">
        <div style="float: right" class="ln-sviesoforas-container">
          <div>
            <button v-if="document.notes != null" type="button" @click="makeToast('info')" class="btn p-0 border-0 bg-transparent" aria-label="Rodyti žymą" aria-roledescription="Mygtukas">
              <font-awesome-icon class="ln-primary" icon="info-circle" aria-hidden="true" />
            </button>
            <span class="ml-3 ln-subtitle-text ln-darkblue">
              <span class="ln-status-name-container" data-testid="status-name">{{ statusName }}</span>
              <font-awesome-icon style="width: 1em" :class="['ln-sviesoforas-' + document.statusCode.toLowerCase(), 'ml-1']"
                icon="circle" aria-hidden="true" />
            </span>
          </div>
          <div class="ln-sviesoforas-date-container" data-testid="status-date">
            <div v-if="document.statusCode.toLowerCase() === 'warning'" style="float: right">
              <span class="ln-subtitle-text-thin">{{ getActualFromForDisplay() }}</span>
            </div>
            <div v-else-if="document.actualTo" style="float: right">
              <span class="ln-subtitle-text-thin">{{ getExpirationDateForDisplay() }}</span>
            </div>
          </div>
        </div>
        <div class="ln-darkblue">
          <div>
            <span class="ln-subtitle-text-thin">{{ document.name ? fixCapitalization(document.name) : "" }}:</span>
            <span class="ml-2 ln-subtitle-text">Nr. {{ document.regNr }} </span>
            <span class="ml-2 ln-subtitle-text-thin">{{ document.signDate }}</span>
          </div>
          <div>
            <span class="ln-subtitle-text-thin">Padalinys:</span>
            <span class="ml-2 ln-subtitle-text">{{
              document.organization ? fixCapitalization(document.organization) : " "
            }}</span>
          </div>
        </div>
        <div class="ln-darkblue d-flex justify-content-start">
          <document-reference-link v-if="renderReferences" :references="document.references"
            :inoperatives="document.inoperatives" :docId="document.id" :appendicesLength="appendicesLength"
            @getReferenceList="getReferenceList" @loadList="loadList" @reloadDocumentEvent="reloadDocument"></document-reference-link>
        </div>
      </div>
      <div v-if="hasCategories">
        <div class="ln-sm-text ln-darkblue px-4 pb-4">
          Dokumento kategorijos priskirtos naudojant DI
          <span v-if="feedbackEmail" class="ln-sm-text-thin">
            (pranešti apie klaidą:
            <a :href="`mailto:${feedbackEmail}`" class="ln-primary" aria-roledescription="Nuoroda" style="text-decoration: underline;">{{ feedbackEmail }}</a>):
          </span>
          <div style="--doc-cat-font-size: 16px;">
            <document-category :categories="document.categories" @searchEvent="$emit('searchEvent', $event)" />
          </div>
        </div>
      </div>
      <div v-if="showStakeholders && document.stakeholders && document.stakeholders.length > 0">
        <div class="ln-bt-g"></div>
        <div @click="stakeholdersOpen = !stakeholdersOpen"
          @keydown.enter="stakeholdersOpen = !stakeholdersOpen"
          @keydown.space.prevent="stakeholdersOpen = !stakeholdersOpen"
          role="button"
          tabindex="0"
          aria-controls="collapse-stakeholders"
          aria-roledescription="Mygtukas"
          :aria-label="'Teisėkūros proceso dalyviai, ' + (stakeholdersOpen ? 'išskleista' : 'suskleista')"
          class="ln-subtitle-text-thin ln-darkblue p-3 ln-pointer">
          <font-awesome-icon v-if="!stakeholdersOpen" :icon="['fas', 'angle-down']" size="lg"
            class="ml-2 mr-2 ln-primary" aria-hidden="true" />
          <font-awesome-icon v-if="stakeholdersOpen" :icon="['fas', 'angle-up']" size="lg"
            class="ml-2 mr-2 ln-primary" aria-hidden="true" />
          Teisėkūros proceso dalyviai
        </div>
        <b-collapse id="collapse-stakeholders" v-model="stakeholdersOpen" class="pb-2">
          <div v-for="(item, index) in stakeholdersList" :key="index"
            class="ln-subtitle-text-thin ln-darkblue ml-5 pb-3">
            <div class="d-flex">
              <div class="ln-subtitle-text">{{ item.type }}:</div>
              <div class="ml-2">
                <div v-for="(person, i) in stakeholdersList[index].list" :key="i">
                  {{ person.vardas }}
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
      </div>
      <div v-if="
        publicationsEnabled && document.publications && document.publications.length > 0
      ">
        <div class="ln-bt-g"></div>
        <div @click="publicationsOpen = !publicationsOpen"
          @keydown.enter="publicationsOpen = !publicationsOpen"
          @keydown.space.prevent="publicationsOpen = !publicationsOpen"
          role="button"
          tabindex="0"
          :aria-expanded="publicationsOpen ? 'true' : 'false'"
          aria-controls="collapse-publications"
          class="ln-subtitle-text-thin ln-darkblue p-3 ln-pointer">
          <font-awesome-icon v-if="!publicationsOpen" :icon="['fas', 'angle-down']" size="lg"
            class="ml-2 mr-2 ln-primary" aria-hidden="true" />
          <font-awesome-icon v-if="publicationsOpen" :icon="['fas', 'angle-up']" size="lg"
            class="ml-2 mr-2 ln-primary" aria-hidden="true" />
          Publikacijos
        </div>
        <b-collapse id="collapse-publications" v-model="publicationsOpen" class="pb-2">
          <div v-for="(item, index) in document.publications" :key="index"
            class="ln-subtitle-text-thin ln-darkblue ml-5 pb-3">
            <span class="ln-subtitle-text">{{ item.leidinys }}</span>, Data: {{ item.data }}, Leidinio Nr.: {{
              item.leidinioNr
            }}, Publikacijos
            Nr.: {{ item.publikacijosNr }}
          </div>
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentReferenceLink from "./document-reference-link";
import { userService } from "../../_Services/user-service";
import { normalizeCapitalization } from "../../_Services/repository";
import appconfig from "../../_Services/appconfig.js";
import DocumentCategory from './document-category.vue';
export default {
  name: "document-meta",
  components: { DocumentReferenceLink, DocumentCategory },
  props: {
    document: Object,
  },
  created() {
    if (!this.document) {
      console.warn("[document-meta] document prop is missing");
      return;
    }
    if (this.showStakeholders) {
      this.prepareStakeholdersListForDisplay();
    }
    if (Array.isArray(this.document.categories)) {
      this.document.categories.forEach(category => {
        category.expanded = false;
      });
    } else {
      this.$set(this.document, 'categories', []);
    }
    if (this.document.notes && this.document.notes.trim()) {
      this.$nextTick(() => {
        this.makeToast("info");
      });
    }
  },
  computed: {
    hasCategories() {
      return this.document.categories && this.document.categories.length > 0;
    },
    appendicesLength() {
      return this.document && this.document.appendices ? this.document.appendices.length : 0;
    },
    publicationsEnabled() {
      return process.env.VUE_APP_SEARCH_PUBLICATION === "true";
    },
    showStakeholders() {
      return process.env.VUE_APP_SHOW_STAKEHOLDERS === "true";
    },
    renderReferences() {
      const hasRefs = this.document.references && this.document.references.length > 0;
      const hasInoperatives = this.document.inoperatives && this.document.inoperatives.length > 0;
      return hasRefs || hasInoperatives || this.appendicesLength > 0;
    },
    statusName() {
      switch (this.document.statusCode.toLowerCase()) {
        case "success": return "GALIOJA";
        case "warning": return "ĮSIGALIOJA";
        case "danger": return "NEGALIOJA";
        default: return this.document.statusName ? this.document.statusName.toUpperCase() : "";
      }
    },
    isAuthenticated() {
      return userService.isAuthorized();
    },
    feedbackEmail() {
      return appconfig.feedbackEmail || null;
    },
  },
  methods: {
    getReferenceList(klasId) {
      this.$emit("getReferenceList", klasId);
    },
    loadList() {
      this.$emit("loadList");
    },
    reloadDocument(id) {this.$emit("reloadDocumentEvent", id);},
    makeToast(variant = "info") {
      this.$bvToast.toast(this.document.notes, {
        title: `Žyma`,
        variant: variant,
        solid: true,
        autoHideDelay: 30000,
      });
    },
    copyTitle() {
      const title = this.document && this.document.title ? this.document.title : "";
      navigator.clipboard.writeText(title);
    },
    reportProblem() {
      if (this.document.id && this.document.version) {
        this.$router.push(`/reportproblem/${this.document.id}/${this.document.version}`);
      }
    },
    prepareStakeholdersListForDisplay() {
      if (!this.showStakeholders || !this.document.stakeholders || !Array.isArray(this.document.stakeholders)) return;
      const typeIds = [...new Set(this.document.stakeholders.map(item => item.tipasId))];
      typeIds.sort();
      typeIds.forEach(typeId => {
        const found = this.document.stakeholders.find(item => item.tipasId === typeId);
        const type = found ? found.tipas : 'Nežinomas';
        const list = this.document.stakeholders.filter(item => item.tipasId === typeId);
        this.stakeholdersList.push({ type, list });
      });
    },
    getExpirationDateForDisplay() {
      if (!this.document.actualTo) return '';
      const expirationDate = new Date(this.document.actualTo.slice(0, 10));
      const now = new Date();
      now.setHours(12);
      expirationDate.setHours(23);
      const isExpired = expirationDate < now;
      if (isExpired) expirationDate.setDate(expirationDate.getDate() + 1);
      const formatted = expirationDate.toISOString().slice(0, 10);
      return isExpired ? `Nuo: ${formatted}` : `Iki: ${formatted}`;
    },
    getActualFromForDisplay() {
      if (!this.document.actualFrom) return '';
      const actualFromDate = new Date(this.document.actualFrom.slice(0, 10));
      const formatted = actualFromDate.toISOString().slice(0, 10);
      return `Nuo: ${formatted}`;
    },
    fixCapitalization(text) {
      return normalizeCapitalization(text);
    },
  },
  data() {
    return {
      publicationsOpen: false,
      stakeholdersOpen: false,
      stakeholdersList: [],
      isClipboardAccessible: location.protocol === "https:",
    };
  },
};
</script>

<style lang="css" scoped>
</style>